/* eslint-disable @typescript-eslint/indent */
import { generatePath, useLocation, useNavigate, useParams } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import { useRefresh } from 'ContextProviders/RefreshProvider'
import { useEffect, useState, useRef } from 'react'
import { toast } from 'react-toastify'
import { type ChildEditValues, AccountType, type location, type postError, type lookup, OtherReasonEndingID, type eligibilityFactor, type race } from 'core/types'
import { sendGet, sendPost, sendPostFormData } from 'hooks/use-fetch'
import { useAccountId } from 'ContextProviders/CurrentAccount'
import { Box, Button, CircularProgress, Grid, type SelectChangeEvent, Typography, DialogActions, RadioGroup, Radio, FormControlLabel, Breadcrumbs, Link } from '@mui/material'
import { DatePickerWithLabel } from 'Components/DatePicker'
import FilePicker from 'Components/FilePicker'
import S3Image from 'Components/S3Image'
import { SelectWithLabel } from 'Components/SelectWithLabel'
import { TextFieldWithLabel } from 'Components/TextField'
import { TextWithLabel } from 'Components/TextWithLabel'
import CancelIcon from '@mui/icons-material/Cancel'
import { type IdentityResponseItem, UICLookupModal } from 'Components/ChildDetails/UICLookupModal'
import { LargeCard } from 'Components/Cards/Large'
import { RaceList } from 'Components/CheckList/RaceList'
import { EligiblityFactorList } from 'Components/CheckList/EligiblityFactorList'
import { ChildAdditionalInfoList } from 'Components/CheckList/ChildAdditionalInfoList'
import * as AppRoutes from 'Pages/Routes'
import { CheckBoxWithLabel } from 'Components/CheckBox'
import { TextAreaWithLabel } from 'Components/TextArea'
import { ActionMenu } from 'Components/Menu/ActionMenu'
import { ActionItem } from 'Components/Menu/ActionItem'
import { ChildMerge } from 'Pages/CareNetworkManagement/CareNetworkDetail/ChildMerge'
import { usePermissions } from 'hooks/use-permissions'
import { dupeSelector, duplicateSource, translateChildEditValues } from 'slices/duplicateChildSlice'
import { useAppSelector } from 'hooks/reduxHooks'
import { useDuplicateCheck } from 'hooks/useDuplicateCheck'
import { LocationDetails } from 'Components/Cards/LocationCard/LocationDetails'
import { MapStateName } from 'Components/Cards/LocationCard/mapStateName'

enum EndStatus {
  None,
  Ended,
  EndedWithOther
}

type ChildEnrollment = ChildEditValues & EnrollmentData

interface EnrollmentData {
  serviceID: number
  programID: number
  schoolYearID: number
}

export function ChildEditPage (): JSX.Element {
  const dupeRecord = useAppSelector(dupeSelector)
  const loadedRaceList: race[] = dupeRecord?.race.split(',').map(r => {
    const newRaceVal: race = {
      codePosition: 0,
      id: 0,
      name: r
    }
    return newRaceVal
  }) ?? []
  const loadedEfList: eligibilityFactor[] = dupeRecord?.eligibilityFactors?.split(',').map(e => {
    const ef: eligibilityFactor = {
      id: 0,
      name: e,
      stateCode: ''
    }
    return ef
  }) ?? []
  const translatedDupe: ChildEditValues | null = dupeRecord != null
    ? {
      ...dupeRecord,
      eligibilityFactorList: loadedEfList,
      childRaceList: loadedRaceList,
      dateOfBirth: dupeRecord.dateOfBirth != null ? new Date(dupeRecord.dateOfBirth) : '',
      dueDate: dupeRecord.dueDate != null ? new Date(dupeRecord.dueDate) : '',
      birthGender: dupeRecord.birthGender.charAt(0)
    } as unknown as ChildEditValues
    : null
  const [params] = useSearchParams()
  const { id } = useParams()
  const [child, setChild] = useState<ChildEditValues | null>(translatedDupe)
  const [menuShown, setMenuShown] = useState(false)
  const [showBornOption, setShowBornOption] = useState(false)
  const [languages, setLanguages] = useState<lookup[]>([])
  const [authStatuses, setAuthStatuses] = useState<lookup[]>([])
  const [unauthorized, setUnauthorized] = useState(false)
  const [allowUICLookup, setAllowUICLookup] = useState<boolean>(false)
  const [showUICLookup, setShowUICLookup] = useState(false)
  const [showConfirmMerge, setShowConfirmMerge] = useState(false)
  const [exitStatus, setExitStatus] = useState<EndStatus>(child?.endDate === null ? EndStatus.None : (child?.reasonEndingID === OtherReasonEndingID ? EndStatus.EndedWithOther : EndStatus.Ended))
  const [reasonsEnding, setReasonsEnding] = useState<lookup[]>([])
  const [enrollmentExitReasons, setEnrollmentExitReasons] = useState<lookup[]>([])
  const childImage = useRef<File | null | undefined>(undefined) // null means the image was removed, undefined means no action was taken
  const initialLoadDone = useRef(false)
  const refreshTime = useRefresh()
  const account = useAccountId()
  const nav = useNavigate()
  const perms = usePermissions()
  const backText = useLocation()?.state?.redirectText ?? 'Previous Page'
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { checkAndHandleDuplicate } = useDuplicateCheck()
  const newRecord = (id == null)
  const [canEditUIC, setCanEditUIC] = useState(false)
  const recordID: number | null = newRecord ? null : Number(id ?? '0')
  const [address, setAddress] = useState<location>({
    id: 0,
    address: dupeRecord?.address ?? '',
    city: dupeRecord?.city ?? '',
    state: dupeRecord?.state ?? '',
    zipCode: dupeRecord?.zipCode ?? '',
    apartment: dupeRecord?.apartment ?? '',
    unitNumber: '',
    note: ''
  })
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      let url = ''
      if (recordID == null) {
        url = '/ChildDetails/NewRecord'
      } else {
        url = `/ChildDetails/EditableRecord?childId=${recordID}`
        if (account?.type === AccountType.ISD) {
          url += `&isdId=${account.id}`
        }
      }
      const { response, success, status } = await sendGet(url, {})
      if (success) {
        const rspChild = response.response as ChildEditValues
        const newVals = translatedDupe != null ? { ...rspChild, ...translatedDupe } : rspChild
        const isFirstLoad = !initialLoadDone.current
        initialLoadDone.current = true
        if (isFirstLoad || recordID != null || translatedDupe != null) {
          setChild(newVals)
          setCanEditUIC(newVals.uic !== null && newVals.uic !== '')
          setExitStatus(newVals?.endDate === null ? EndStatus.None : (newVals?.reasonEndingID === OtherReasonEndingID ? EndStatus.EndedWithOther : EndStatus.Ended))
          setShowBornOption(newRecord || (newVals?.isUnborn ?? false))
        }
        if (newVals?.childLocations != null && newVals.childLocations.length > 0) {
          rspChild.childLocations[0].isPrimary = true
          rspChild.childLocations[0].districtName = rspChild.residentDistrict ?? undefined
          rspChild.childLocations[0].isdName = rspChild.residentISD ?? undefined
          rspChild.childLocations[0].apartment = rspChild.childLocations[0].unitNumber ?? ''
          setAddress(rspChild.childLocations[0])
        }
      } else if (status === 401) {
        setUnauthorized(true)
      } else {
        const errors: string[] = response.errors
        errors.forEach(e => toast.error(e))
      }

      const { response: rsp1, error: err1 } = await sendGet('/ChildDetails/AllowUICLookup', {})
      if (err1[0] === '') {
        setAllowUICLookup(rsp1)
      }
      const { response: rsp2, error: err2 } = await sendGet('/Language/GetLookup?sort=true', {})
      if (err2[0] === '') {
        setLanguages(rsp2)
      }
      const { response: rsp3, error: err3 } = await sendGet('/AuthorizationStatus/GetLookup', {})
      if (err3[0] === '') {
        setAuthStatuses(rsp3)
      }
      const { response: rsp4, error: err4 } = await sendGet('/ChildDetails/ReasonsEnding', {})
      if (err4[0] === '') {
        setReasonsEnding(rsp4)
      }
      if (recordID != null) {
        const { response: rsp5, error: err5 } = await sendGet(`/EnrollmentSearch/ExitReasonForChild?childID=${recordID}`, {})
        if (err5[0] === '' && rsp5 != null) {
          setEnrollmentExitReasons(rsp5)
        }
      }
    }

    void fetchData()
  }, [refreshTime])

  const genderOptions: Array<{ id: string, name: string }> = [
    { id: 'M', name: 'Male' },
    { id: 'F', name: 'Female' }
  ]

  const handleChange = (e: any): void => {
    if (child !== null) {
      setChild({ ...child, [e.target.name]: e.target.value })
    }
  }

  const handleDateChange = (valueName: string, newDate: Date | null): void => {
    if (child !== null) {
      setChild({ ...child, [valueName]: newDate })
    }
  }

  const handleSelectChange = (e: SelectChangeEvent<number | string | null>): void => {
    if (child !== null) {
      setChild({ ...child, [e.target.name]: e.target.value })
    }
  }

  const hideMenu = (): void => {
    setMenuShown(false)
  }

  const onShow = (): void => {
    setMenuShown(true)
  }

  const setExiting = (newValue: boolean): void => {
    if (newValue) {
      setExitStatus(EndStatus.Ended)
    } else {
      setExitStatus(EndStatus.None)
    }
  }

  const reasonEndingChange = (newValue: number): void => {
    if (child !== null) {
      setChild({ ...child, reasonEndingID: newValue })

      if (newValue === OtherReasonEndingID) {
        setExitStatus(EndStatus.EndedWithOther)
      } else {
        setExitStatus(EndStatus.Ended)
      }
    }
  }

  const setChildImage = (files: FileList | null): void => {
    if (files !== null && child !== null) {
      childImage.current = files[0]
      setChild({ ...child, imageFile: files[0].name })
    }
  }

  const removeChildImage = (): void => {
    if (child !== null) {
      childImage.current = null
      setChild({ ...child, imageFile: null })
    }
  }

  const lookupUIC = async (): Promise<void> => {
    if (child !== null) {
      const { response } = await sendPost('/ChildDetails/ValidateChild', child)
      const errors: postError[] = response as postError[]
      const messageError = errors.filter(f => f.group !== 'Child')
      if (messageError.length > 0) {
        toast.error(messageError[0].error)
      }
      const requiredValueErrors = errors.filter(f => f.group === 'Child')
      if (requiredValueErrors.length > 0) {
        toast.error('Please fill out all required fields')
      }
      // dispatch({ type: 'errors', errors })
      if (errors.length === 0) {
        setShowUICLookup(true)
      }
    }
  }

  const childMatched = (uic: string | null, apiResponse: IdentityResponseItem | null, updateValues: boolean): void => {
    setShowUICLookup(false)
    if (uic !== null && child !== null) {
      const editChild = { ...child }
      editChild.uic = uic
      if (updateValues && apiResponse !== null) {
        editChild.dateOfBirth = apiResponse.birthDate
        editChild.firstName = apiResponse.firstName
        editChild.middleName = apiResponse.middleName
        editChild.lastName = apiResponse.lastSurname
        editChild.suffix = apiResponse.generationCodeSuffix
        editChild.birthGender = apiResponse.sexType
      }
      setChild(editChild)
    }
  }

  const showMergeDialog = (): void => {
    setShowConfirmMerge(true)
    hideMenu()
  }

  const returnToDetailPage = (childID?: number): void => {
    let detailPage: string
    const enrollmentID = params.get('enrollmentID')
    if (enrollmentID != null) {
      detailPage = `${generatePath(AppRoutes.ENROLLMENT, { id: enrollmentID })}?childID=${String(id)}`
    } else if (childID == null && recordID == null) {
      detailPage = generatePath(AppRoutes.FIND_CHILDREN)
    } else {
      detailPage = generatePath(AppRoutes.CARENETWORK_MANAGEMENT_DETAILS, { id: (childID == null ? (String(recordID) ?? '0') : String(childID)) })
    }
    nav(detailPage)
  }

  enum ChildPostMode {
    Insert,
    Update,
    InsertWithEnrollment
  }

  const saveChanges = async (): Promise<void> => {
    if (child !== null && !isSubmitting) {
      setIsSubmitting(true)
      child.childLocations = [address]

      const { response: validation } = await sendPost('/ChildDetails/validate', child)

      // Validate
      if (validation.errors.length > 0) {
        validation.errors.forEach((e: postError) => toast.error(e.error))
        setIsSubmitting(false)
        return
      }
      const source = newRecord || child.id === 0 ? duplicateSource.ChildEntry : duplicateSource.ChildEdit
      const childDupeRecord = translateChildEditValues(child, source, params)
      if (await checkAndHandleDuplicate(childDupeRecord)) return

      let newRecordID: number | null = null
      let postObject: any
      let url = '/ChildDetails/'
      let action: ChildPostMode
      if (!newRecord || params.get('isdID') == null || params.get('serviceID') == null || params.get('programID') == null || params.get('schoolYearID') == null) {
        if (newRecord) {
          action = ChildPostMode.Insert
          url += 'Insert'
        } else {
          action = ChildPostMode.Update
          url += 'Update'
        }
        postObject = child
      } else {
        action = ChildPostMode.InsertWithEnrollment
        const enrollment: ChildEnrollment = {
          ...child,
          isdID: Number(params.get('isdID')),
          serviceID: Number(params.get('serviceID')),
          programID: Number(params.get('programID')),
          schoolYearID: Number(params.get('schoolYearID'))
        }
        url += 'InsertWithEnrollment'
        postObject = enrollment
      }
      const { response } = await sendPost(url, postObject)
      if (response.success === false) {
        response.errors.forEach((e: postError) => toast.error(e.error))
        setIsSubmitting(false)
      } else {
        if (action === ChildPostMode.Update) {
          newRecordID = recordID
        } else {
          newRecordID = Number(response.newRecordID)
        }
        // for childImage, undefined means no action was taken, null means the image was removed, having a value means it was updated
        if (childImage.current !== undefined) {
          const fileData = new FormData()
          fileData.append('id', child.id.toString())
          if (childImage.current === null) {
            await sendPostFormData('/ChildDetails/RemoveImage', fileData)
          } else {
            fileData.append('file', childImage.current)
            await sendPostFormData('/ChildDetails/UploadImage', fileData)
          }
        }
        // since we successfully saved, dont ever release the submitting item and wait for the navigate to run
        if (action === ChildPostMode.InsertWithEnrollment) {
          const detailPage = generatePath(AppRoutes.ENROLLMENT, { id: String(newRecordID) })
          nav(detailPage)
        } else {
          returnToDetailPage(newRecordID ?? 0)
        }
      }
    }
  }

  function bornStatusChange (event: any, value: string): void {
    if (child !== null) {
      setChild({ ...child, isUnborn: (value === 'true'), dateOfBirth: (value === 'true' ? null : new Date()) })
    }
  }
  function goBack (): void {
    nav(-1)
  }

  const handleTribeChange = (checked: boolean, value: number | null): void => {
    if (child !== null) {
      setChild({ ...child, tribalAffiliationID: value, tribalAffiliationStatus: checked })
    }
  }
  const title = newRecord ? 'Add Child Information' : 'Edit Child Information'
  return <>
    <Grid container flex='row' sx={{ justifyContent: 'space-between' }}>
      <Grid item>
        <Typography variant="h3">{title}</Typography>
      </Grid>
      {(perms.CareNetwork_MergeRecords || (child?.isUnborn ?? false)) &&
        <Grid item>
          <ActionMenu open={menuShown} onClose={hideMenu} onShow={onShow} testId='caseManagement' menuProps={{ border: 'none' }}>
            <Box>
              {perms.CareNetwork_MergeRecords && <ActionItem testId='mergeRecord' onClick={showMergeDialog} >Delete Record</ActionItem>}
            </Box>
          </ActionMenu>
        </Grid>
      }
    </Grid>
    <Breadcrumbs>
      <Link onClick={goBack} sx={{ textTransform: 'none', color: 'inherit', textDecorationLine: 'none', cursor: 'pointer' }}>Return to {backText}</Link>
    </Breadcrumbs>

    {child === null
      ? <CircularProgress />
      : unauthorized
        ? <div>You are not authorized to view this page</div>
        : <>
          <Grid container spacing={2} >
            <Grid item xs={12} sm={12} md={6} sx={{ mt: '16px', pt: '0px !important' }}>
              <LargeCard
                header='Child Details'
                fullWidth={true}
                content={
                  <Grid container spacing={2} sx={{ width: 'calc(100% - 16px)' }}> { /* setting the width here fixes issue where controls are too wide and getting cut off */}
                    {showBornOption && <Grid item xs={6}>
                      <RadioGroup value={child.isUnborn} onChange={bornStatusChange} defaultValue={false} name='isUnborn' sx={{ paddingTop: '10px' }}>
                        <FormControlLabel value={false} control={<Radio />} label='This child has been born' />
                        <FormControlLabel value={true} control={<Radio />} label='Unborn child' />
                      </RadioGroup>
                    </Grid>
                    }
                    <Grid item xs={6}>
                      <TextFieldWithLabel
                        label='First Name'
                        name='firstName'
                        maxLength={50}
                        value={child.firstName ?? ''}
                        onChange={handleChange}
                        required={!child.isUnborn}
                        data-testid="firstName"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextFieldWithLabel
                        label='Last Name'
                        data-testid='lastName'
                        name='lastName'
                        maxLength={50}
                        value={child.lastName ?? ''}
                        onChange={handleChange}
                        required={true}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextFieldWithLabel
                        label='Middle Name'
                        name='middleName'
                        maxLength={50}
                        value={child.middleName ?? ''}
                        onChange={handleChange}
                        dataTestId='middleName'
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextFieldWithLabel
                        label='Suffix'
                        name='suffix'
                        maxLength={50}
                        value={child.suffix ?? ''}
                        onChange={handleChange}
                        dataTestId='suffix'
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextFieldWithLabel
                        label='Nick Name'
                        name='nickName'
                        maxLength={50}
                        value={child.nickName ?? ''}
                        onChange={handleChange}
                        dataTestId='nickName'
                      />
                    </Grid>
                    {child.isUnborn &&
                      <Grid item xs={6}>
                        <DatePickerWithLabel
                          label='Due Date'
                          onChange={(newValue) => { handleDateChange('dueDate', newValue) }}
                          value={child.dueDate ?? null}
                          name='dueDate'
                          dataTestId="dueDate"
                          required={true}
                        />
                      </Grid>
                    }
                    {!child.isUnborn &&
                      <>
                        <Grid item xs={6}>
                          <DatePickerWithLabel
                            label='Date of Birth'
                            onChange={(newValue) => { handleDateChange('dateOfBirth', newValue) }}
                            value={child.dateOfBirth ?? null}
                            name='dateOfBirth'
                            dataTestId="dateOfBirth"
                            required={true}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <SelectWithLabel
                            name="birthGender"
                            label="Assigned Gender at Birth"
                            data-testid="birthGender"
                            value={child.birthGender ?? ''}
                            options={genderOptions}
                            onChange={handleSelectChange}
                            required={true}
                            maxWidth='100%'
                            width='100%'
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <SelectWithLabel
                            name="languageID"
                            label="Primary Language (if not English)"
                            width="97%"
                            required={false}
                            includeNoneOption={true}
                            value={child.languageID ?? ''}
                            options={languages}
                            onChange={handleSelectChange}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <div>Child Image</div>
                          <Box sx={{ pt: '8px', flexDirection: 'column', display: 'flex', width: '100%' }}>
                            {child.imageFile != null && <Box>
                              <Typography>Uploading {child.imageFile}</Typography>
                            </Box>}
                            <FilePicker
                              label='Click here to change'
                              name='file'
                              buttonVariant='text'
                              onSelect={(e: any) => {
                                setChildImage(e.target.files)
                              }}
                            />
                            {child.image !== null && child.image !== undefined && child.image !== '' && (<>
                              <S3Image imageKey={child.image} />
                              <Button onClick={removeChildImage} data-testid='remove-logo' sx={{ paddingLeft: 0, verticalAlign: 'top', minWidth: '45px' }}><CancelIcon sx={{ color: 'black' }} /></Button>
                            </>)}
                          </Box>
                        </Grid>
                      </>
                    }
                    <Grid item xs={12}>
                      <SelectWithLabel
                        name="authorizationStatusID"
                        label="Authorization Status"
                        width="97%"
                        required={false}
                        includeNoneOption={true}
                        value={child.authorizationStatusID ?? ''}
                        options={authStatuses}
                        onChange={handleSelectChange}
                      />
                    </Grid>
                    {allowUICLookup && !child.isUnborn &&
                      <>
                        <Grid item xs={6}>
                          {!canEditUIC
                            ? <>
                              <TextWithLabel
                                label='UIC'
                                name='uic'
                                testId='uic'
                                toolTip='UIC is a unique identifier for this child.  It is used to match children across systems.'
                                value={child.uic === null || child.uic === '' ? '(none)' : child.uic}
                              />
                              <Button onClick={lookupUIC} variant='text'>Click here to get UIC </Button>
                            </>
                            : <TextFieldWithLabel
                                label='UIC'
                                name='uic'
                                maxLength={10}
                                value={child.uic}
                                onChange={handleChange}
                                toolTip='UIC is a unique identifier for this child.  It is used to match children across systems.'
                                dataTestId='uic'
                            />}
                        </Grid>

                        {showUICLookup && <UICLookupModal onClose={childMatched} child={child} />}
                      </>
                    }
                    <Grid item xs={12}>
                      <RaceList
                        raceList={child.childRaceList ?? []}
                        setRaceList={(raceList) => { setChild({ ...child, childRaceList: raceList }) }}
                        setTribeStatus={handleTribeChange}
                        tribalAffiliationID={child.tribalAffiliationID}
                        tribalAffiliationStatus={child.tribalAffiliationStatus}

                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography className="info-box-header">Primary Address</Typography>
                      <LocationDetails
                        onChange={(e) => {
                          if (e.target.name === 'apartment') {
                            // handle a situation where we have both apartment and unitNumber on the front end and the back end expects unitNumber
                            setAddress({ ...address, [e.target.name]: e.target.value, unitNumber: e.target.value })
                          } else {
                            setAddress({ ...address, [e.target.name]: e.target.value })
                          }
                        }}
                        onPlaceSelected={async (place: Microsoft.Maps.ISuggestionResult) => {
                          const newplace: location = { ...address }
                          newplace.address = place.address.addressLine
                          newplace.city = place.address.locality
                          newplace.apartment = ''
                          newplace.state = MapStateName(place.address.adminDistrict)
                          newplace.zipCode = place.address.postalCode
                          setAddress(newplace)
                        }}
                        editData={address}
                        childID={recordID ?? 0}
                        showHomelessOption={true}
                        hidePrimary={true}
                      />
                    </Grid>
                  </Grid>
                }
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <LargeCard header="Additional Information"
                fullWidth
                content={
                  <div style={{ width: 'calc(100% - 24px)' }}> { /* setting the width here fixes issue where controls are too wide and getting cut off */}
                    <EligiblityFactorList
                      eligiblityList={child.eligibilityFactorList}
                      otherFactor={child.otherEligibilityFactor}
                      enviromentalRiskList={child.environmentalRiskList}
                      captureIEP={child.captureIEP}
                      captureIFSP={child.captureIFSP}
                      captureEOIS={child.captureEOIS}
                      hasIEP={child.hasIEP}
                      hasIFSP={child.hasIFSP}
                      hasEOIS={child.hasEOIS}
                      iepRecommendsInclusivePlacement={child.iepRecommendsInclusivePlacement}
                      setEligibilityValues={(factors, envRisks, values) => {
                        setChild({ ...child, eligibilityFactorList: factors, environmentalRiskList: envRisks, otherEligibilityFactor: values.otherFactor, hasIEP: values.hasIEP, hasIFSP: values.hasIFSP, hasEOIS: values.hasEOIS, iepRecommendsInclusivePlacement: values.iepRecommendsInclusivePlacement })
                      }}
                    />
                    <Box sx={{ marginTop: '10px' }} >
                      <ChildAdditionalInfoList
                        values={{
                          hasSNAPBenefits: child.hasSNAPBenefits,
                          isFosterChild: child.isFosterChild,
                          isOnCashAssistance: child.isOnCashAssistance,
                          isOnSSI: child.isOnSSI,
                          isImmigrant: child.isImmigrant
                        }}
                        setAdditionalValues={(values) => {
                          setChild({ ...child, hasSNAPBenefits: values.hasSNAPBenefits, isFosterChild: values.isFosterChild, isOnCashAssistance: values.isOnCashAssistance, isOnSSI: values.isOnSSI, isImmigrant: values.isImmigrant })
                        }}
                      />
                    </Box>
                    <Box sx={{ marginTop: '20px' }} >
                      <Typography className="info-box-header">MiECC Exit Information</Typography>
                      {(account?.type !== AccountType.SERVICE_PROVIDER) &&
                        <CheckBoxWithLabel
                          label='Child is Exiting MIECC Service'
                          name='exiting'
                          edge='start'
                          dataTestId='childExiting'
                          value={exitStatus !== EndStatus.None}
                          setValue={(val) => { setExiting(val) }}
                        />
                      }
                      {exitStatus !== EndStatus.None && <>
                        <DatePickerWithLabel
                          label='Exit Date'
                          onChange={(newValue) => { handleDateChange('endDate', newValue) }}
                          value={child.endDate ?? null}
                          name='endDate'
                          dataTestId="endDate"
                          required={true}
                        />
                        <SelectWithLabel
                          name="reasonEndingID"
                          label="Reason Ending"
                          value={child.reasonEndingID ?? ''}
                          width="100%"
                          required={true}
                          options={reasonsEnding}
                          onChange={(e) => { reasonEndingChange(Number(e.target.value)) }}
                        />
                        {exitStatus === EndStatus.EndedWithOther &&
                          <TextAreaWithLabel
                            label='Other Ending Reason'
                            name='reasonEndingComment'
                            dataTestId='reasonEndingComment'
                            onChange={handleChange}
                            value={child.reasonEndingComment}
                          />
                        }
                        {enrollmentExitReasons.length > 0 &&
                          <SelectWithLabel
                            name="exitReasonID"
                            label="This child has an active enrollment.  Select the exit reason to apply to it"
                            value={child.exitReasonID ?? ''}
                            width="100%"
                            required={true}
                            options={enrollmentExitReasons}
                            labelSx={{ whiteSpace: 'break-spaces' }}
                            onChange={handleSelectChange}
                          />
                        }

                      </>
                      }
                    </Box>
                  </div>
                }
              />
            </Grid>
            <Grid item md={12} sm={12} xs={12}>
              <DialogActions sx={{ justifyContent: 'space-between' }}>
                <Button
                  data-testid='cancelButton'
                  variant='outlined'
                  color='inherit'
                  onClick={() => { goBack() }}
                  sx={{ fontWeight: 'bold', textTransform: 'none' }}
                >Cancel
                </Button>
                <Button
                  data-testid='updateButton'
                  color='secondary'
                  variant='contained'
                  disabled={isSubmitting}
                  onClick={saveChanges}
                  sx={{ textTransform: 'none' }}
                >
                  {isSubmitting && <CircularProgress sx={{ height: '20px !important', width: '20px !important' }} color='success' />}
                  Save Child Information
                </Button>
              </DialogActions>
            </Grid>
          </Grid>

          {showConfirmMerge &&
            <ChildMerge
              open={showConfirmMerge}
              child={child}
              onClose={(deleted: boolean) => {
                if (deleted) {
                  nav('/CareNetworkManagement')
                } else {
                  setShowConfirmMerge(false)
                }
              }}
            />
          }
        </>
    }
  </>
}
